import { Component, type ReactNode } from 'react'
import { ErrorPage } from './ErrorPage'
import { InlineErrorCard } from './InlineErrorCard'
import { isStaleChunkError, reloadForStaleChunk } from '@/lib/staleChunk'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  variant?: 'page' | 'inline'
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (isStaleChunkError(error)) reloadForStaleChunk()
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleRetry = () => {
    // A missing chunk stays missing until the page reloads.
    if (isStaleChunkError(this.state.error)) {
      window.location.reload()
      return
    }
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      if (this.props.variant === 'inline') {
        return (
          <InlineErrorCard message={this.state.error?.message || 'An unexpected error occurred'} />
        )
      }

      return (
        <ErrorPage
          title="Something went wrong"
          message={this.state.error?.message || 'An unexpected error occurred'}
          onRetry={this.handleRetry}
        />
      )
    }

    return this.props.children
  }
}
